package com.evoapps.evoshift.domain.model

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil
import kotlinx.datetime.number
import kotlinx.datetime.plus

/*
 * SH-227: local events and special days. A day can be marked by events the owner or a manager
 * typed in (Open'er, a match, a fair: `workplaces/{id}/local_events/{eventId}`) and, with the
 * workplace's "school breaks and long weekends" on, by the days that change how busy a place is
 * every year: the winter break of the workplace's voivodeship, the summer school holidays and the
 * long weekends (with their bridge days). Nothing here is fetched at runtime: the school breaks
 * ship with the app (domain/data/special-days), the long weekends follow from the holiday calendar.
 */

/**
 * An event near the workplace that changes the day's trade, typed in by an owner or a manager.
 * [from] to [to] inclusive; [yearly] repeats it every year on the same dates (29 February falls on
 * the 28th in other years) from its first year on, never before.
 */
data class LocalEvent(
    val id: String,
    val workplaceId: String,
    val name: String,
    val from: LocalDate,
    val to: LocalDate,
    val yearly: Boolean = false,
    val createdBy: String = "",
    /** ISO instant. */
    val createdAt: String = ""
) {
    /** How many days after [from] the event ends (0: one day). */
    val lengthDays: Int get() = from.daysUntil(to).coerceAtLeast(0)

    /** The event's dates when it starts in [year] (a yearly event's repeat), or null before its first year. */
    fun occurrenceStartingIn(year: Int): ClosedRange<LocalDate>? {
        if (!yearly) return if (from.year == year) from..to else null
        if (year < from.year) return null
        val start = if (year == from.year) from else sameDayIn(from, year)
        return start..start.plus(DatePeriod(days = lengthDays))
    }

    /** The event's dates that overlap [range] (several for a yearly event over a long range). */
    fun occurrencesIn(range: ClosedRange<LocalDate>): List<ClosedRange<LocalDate>> {
        if (!yearly) return if (to >= range.start && from <= range.endInclusive) listOf(from..to) else emptyList()
        return ((range.start.year - 1)..range.endInclusive.year).mapNotNull { year ->
            occurrenceStartingIn(year)?.takeIf { it.endInclusive >= range.start && it.start <= range.endInclusive }
        }
    }

    /** The occurrence that covers [date], if any. */
    fun occurrenceOn(date: LocalDate): ClosedRange<LocalDate>? = occurrencesIn(date..date).firstOrNull()

    fun occursOn(date: LocalDate): Boolean = occurrenceOn(date) != null

    companion object {
        /** The longest name (the marker shows a few letters of it; the line on tap the whole). */
        const val MAX_NAME_LENGTH = 60
        /** The longest event: a festival or a fair, not a season. */
        const val MAX_DAYS = 31

        private fun sameDayIn(date: LocalDate, year: Int): LocalDate {
            val month = date.month.number
            val day = if (month == 2 && date.day == 29 && !isLeap(year)) 28 else date.day
            return LocalDate(year, month, day)
        }

        private fun isLeap(year: Int) = (year % 4 == 0 && year % 100 != 0) || year % 400 == 0
    }
}

/** What marks a day; the order is the marker's priority (what is shown when a day has several). */
enum class SpecialDayKind {
    /** Typed in by an owner or a manager. */
    LOCAL_EVENT,
    /** A public holiday next to a weekend, or a bridge day between them. */
    LONG_WEEKEND,
    /** The voivodeship's two weeks of winter break (ferie zimowe). */
    WINTER_BREAK,
    /** The summer school holidays (wakacje). */
    SUMMER_HOLIDAYS
}

/**
 * One reason a day is special, with the whole period it belongs to ([from]..[to]) for the line on
 * tap ("Open'er, 1–4 Jul"). [name]: the event's name; [eventId]: the event (editable);
 * [bridge]: a long weekend's working day between a holiday and the weekend; [holidayName]: the
 * long weekend's holiday as the legal calendar names it; [voivodeship]: whose winter break.
 */
data class SpecialDay(
    val kind: SpecialDayKind,
    val from: LocalDate,
    val to: LocalDate,
    val name: String? = null,
    val eventId: String? = null,
    val bridge: Boolean = false,
    val holidayName: String? = null,
    val voivodeship: Voivodeship? = null
)

/** Everything that marks [date], the marker's first ([primary]) on top. Never empty. */
data class DaySpecials(val date: LocalDate, val items: List<SpecialDay>) {
    val primary: SpecialDay get() = items.first()

    /** The days's events typed in (editable), in the marker's order. */
    val events: List<SpecialDay> get() = items.filter { it.kind == SpecialDayKind.LOCAL_EVENT }

    /** How many more than the marker shows. */
    val more: Int get() = items.size - 1
}

/** Poland's 16 voivodeships; [polishName] as the Ministry of Education lists them. */
enum class Voivodeship(val polishName: String, val englishName: String) {
    DOLNOSLASKIE("dolnośląskie", "Lower Silesian"),
    KUJAWSKO_POMORSKIE("kujawsko-pomorskie", "Kuyavian-Pomeranian"),
    LUBELSKIE("lubelskie", "Lublin"),
    LUBUSKIE("lubuskie", "Lubusz"),
    LODZKIE("łódzkie", "Łódź"),
    MALOPOLSKIE("małopolskie", "Lesser Poland"),
    MAZOWIECKIE("mazowieckie", "Masovian"),
    OPOLSKIE("opolskie", "Opole"),
    PODKARPACKIE("podkarpackie", "Subcarpathian"),
    PODLASKIE("podlaskie", "Podlaskie"),
    POMORSKIE("pomorskie", "Pomeranian"),
    SLASKIE("śląskie", "Silesian"),
    SWIETOKRZYSKIE("świętokrzyskie", "Holy Cross"),
    WARMINSKO_MAZURSKIE("warmińsko-mazurskie", "Warmian-Masurian"),
    WIELKOPOLSKIE("wielkopolskie", "Greater Poland"),
    ZACHODNIOPOMORSKIE("zachodniopomorskie", "West Pomeranian");

    companion object {
        fun fromName(name: String?): Voivodeship? = entries.firstOrNull { it.name == name }
    }
}
